import { useEffect, useState, useRef, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ideas as ideasApi, paradigm, conversations, Idea, Conversation, Message, Claim, ClaimsResponse } from '../lib/api'
import Scratchpad from '../components/Scratchpad'
import ConversationSidebar from '../components/ConversationSidebar'
import TodoCard from '../components/TodoCard'
import ReactMarkdown from 'react-markdown'

// --- Types ---

type DocType = 'CB' | 'SP' | 'BP'
type LayoutMode = 'chat' | 'edit' | 'split' | 'todos' | 'claims'

interface Todo {
  id: string
  question: string
  options: string[]
  context?: string
  idea?: string
  blocking?: string[]
  agent_reasoning?: string
}

const DOC_INFO: Record<DocType, { title: string; description: string }> = {
  CB: { title: 'Concept Brief', description: 'Core concept, problem space, and vision.' },
  SP: { title: 'Strategic Plan', description: 'Value proposition, market context, and success metrics.' },
  BP: { title: 'Blueprint', description: 'Architecture, implementation, resources, and timeline.' },
}

const MODE_LABELS: Record<LayoutMode, string> = {
  chat: 'Chat',
  edit: 'Canon',
  split: 'Split',
  todos: 'TODOs',
  claims: 'Claims',
}

// Fidelity color helpers
function fidelityColor(f: number): string {
  if (f >= 0.7) return 'text-emerald-400'
  if (f >= 0.4) return 'text-yellow-400'
  return 'text-red-400'
}

function fidelityBg(f: number): string {
  if (f >= 0.7) return 'bg-emerald-950 border-emerald-800'
  if (f >= 0.4) return 'bg-yellow-950 border-yellow-800'
  return 'bg-red-950 border-red-800'
}

// Claim type badge colors
const TYPE_COLORS: Record<string, string> = {
  mechanism: 'bg-blue-950 text-blue-400 border-blue-800',
  strategy: 'bg-purple-950 text-purple-400 border-purple-800',
  invariant: 'bg-red-950 text-red-400 border-red-800',
  problem: 'bg-orange-950 text-orange-400 border-orange-800',
  goal: 'bg-emerald-950 text-emerald-400 border-emerald-800',
  vision: 'bg-cyan-950 text-cyan-400 border-cyan-800',
  roadmap: 'bg-indigo-950 text-indigo-400 border-indigo-800',
  unknown: 'bg-gray-800 text-gray-400 border-gray-700',
}

// --- Idea Workspace ---

export default function IdeaWorkspacePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [idea, setIdea] = useState<Idea | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Layout
  const [mode, setMode] = useState<LayoutMode>('chat')
  const [scratchpadOpen, setScratchpadOpen] = useState(false)
  const [convoSidebarOpen, setConvoSidebarOpen] = useState(false)

  // Chat
  const [conversation, setConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [chatInput, setChatInput] = useState('')
  const [sending, setSending] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Canon editor
  const [activeDoc, setActiveDoc] = useState<DocType>('CB')
  const [editedCanon, setEditedCanon] = useState<Record<DocType, string>>({ CB: '', SP: '', BP: '' })
  const [savingCanon, setSavingCanon] = useState(false)

  // TODOs
  const [todos, setTodos] = useState<Todo[]>([])
  const [todosLoading, setTodosLoading] = useState(false)
  const [todoCount, setTodoCount] = useState(0)

  // Claims
  const [claims, setClaims] = useState<Claim[]>([])
  const [claimsLoading, setClaimsLoading] = useState(false)
  const [claimTypeFilter, setClaimTypeFilter] = useState<string>('all')
  const [claimSort, setClaimSort] = useState<'section' | 'fidelity'>('section')

  // Load idea
  useEffect(() => {
    if (!id) return
    let cancelled = false

    const load = async () => {
      try {
        const data = await ideasApi.get(id)
        if (cancelled) return
        setIdea(data)
        setEditedCanon({
          CB: data.canon?.CB || '',
          SP: data.canon?.SP || '',
          BP: data.canon?.BP || '',
        })
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load idea')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [id])

  // Load TODOs for this idea (fetch on mount to get count for badge)
  const loadTodos = useCallback(async () => {
    if (!idea) return
    setTodosLoading(true)
    try {
      // Use the idea title (paradigm ideas) or idea_id for filtering
      const ideaName = idea.user_id === 'system' ? idea.title : undefined
      const data = await paradigm.getTodos(ideaName)
      const todoList = Array.isArray(data) ? data : []
      setTodos(todoList)
      setTodoCount(todoList.length)
    } catch {
      setTodos([])
      setTodoCount(0)
    } finally {
      setTodosLoading(false)
    }
  }, [idea])

  useEffect(() => {
    if (idea) loadTodos()
  }, [idea, loadTodos])

  // Load claims for this idea
  const loadClaims = useCallback(async () => {
    if (!id) return
    setClaimsLoading(true)
    try {
      const data: ClaimsResponse = await ideasApi.getClaims(id)
      setClaims(data.claims || [])
    } catch {
      setClaims([])
    } finally {
      setClaimsLoading(false)
    }
  }, [id])

  useEffect(() => {
    if (id) loadClaims()
  }, [id, loadClaims])

  // Load or create conversation for this idea
  useEffect(() => {
    if (!id || !idea) return
    let cancelled = false

    const loadConvo = async () => {
      try {
        const convos = await conversations.list(id)
        if (cancelled) return
        if (convos && convos.length > 0) {
          setConversation(convos[0])
          const data = await conversations.get(convos[0].id)
          if (!cancelled) setMessages(data.messages || [])
        }
      } catch {
        // No conversation yet
      }
    }

    loadConvo()
    return () => { cancelled = true }
  }, [id, idea])

  // Scroll chat to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Send chat message
  const handleSendMessage = useCallback(async () => {
    if (!chatInput.trim() || !id || sending) return
    const text = chatInput.trim()
    setChatInput('')
    setSending(true)

    try {
      let convoId = conversation?.id

      if (!convoId) {
        const newConvo = await conversations.create({ idea_id: id, title: idea?.title || 'Chat' })
        setConversation(newConvo)
        convoId = newConvo.id
      }

      const tempId = `temp-${Date.now()}`
      const userMsg: Message = {
        id: tempId,
        conversation_id: convoId!,
        role: 'user',
        content: text,
        created_at: new Date().toISOString(),
      }
      setMessages((prev) => [...prev, userMsg])

      const response = await conversations.sendMessage(convoId!, text)

      if (response) {
        setMessages((prev) => {
          const withoutOptimistic = prev.filter((m) => m.id !== userMsg.id)
          const newMessages = [...withoutOptimistic]
          if (response.user_message) newMessages.push(response.user_message)
          else newMessages.push(userMsg)
          if (response.assistant_message) newMessages.push(response.assistant_message)
          return newMessages
        })
      }
    } catch (e) {
      console.error('Failed to send message:', e)
      setMessages((prev) => prev.filter((m) => !m.id.startsWith('temp-')))
    } finally {
      setSending(false)
    }
  }, [chatInput, id, idea, conversation, sending])

  // Save canon
  const handleSaveCanon = async () => {
    if (!id) return
    setSavingCanon(true)
    try {
      await ideasApi.updateCanon(id, editedCanon)
      setIdea((prev) => prev ? { ...prev, canon: editedCanon } : prev)
    } catch (e) {
      console.error('Failed to save canon:', e)
    } finally {
      setSavingCanon(false)
    }
  }

  // Resolve TODO
  const handleResolveTodo = async (todoId: string, selectedOption: number | null, notes?: string) => {
    await paradigm.resolveTodo(todoId, selectedOption, notes)
    setTodos((prev) => prev.filter((t) => t.id !== todoId))
    setTodoCount((prev) => Math.max(0, prev - 1))
  }

  // Switch conversation from sidebar
  const handleSelectConversation = useCallback(async (convo: Conversation) => {
    setConversation(convo)
    try {
      const data = await conversations.get(convo.id)
      setMessages(data.messages || [])
    } catch {
      setMessages([])
    }
  }, [])

  // Create new conversation from sidebar
  const handleCreateConversation = useCallback(async () => {
    if (!id) return
    try {
      const newConvo = await conversations.create({ idea_id: id, title: idea?.title || 'New Chat' })
      setConversation(newConvo)
      setMessages([])
    } catch (e) {
      console.error('Failed to create conversation:', e)
    }
  }, [id, idea])

  const canonIsDirty =
    idea?.canon?.CB !== editedCanon.CB ||
    idea?.canon?.SP !== editedCanon.SP ||
    idea?.canon?.BP !== editedCanon.BP

  // Filtered and sorted claims
  const displayClaims = claims
    .filter((c) => claimTypeFilter === 'all' || c.type === claimTypeFilter)
    .sort((a, b) => {
      if (claimSort === 'fidelity') return a.fidelity - b.fidelity
      // Sort by section string (e.g., "§0", "§1.1", "§2")
      return a.section.localeCompare(b.section)
    })

  const claimTypes = Array.from(new Set(claims.map((c) => c.type))).sort()

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
          <p className="mt-3 text-gray-400 text-sm">Loading idea...</p>
        </div>
      </div>
    )
  }

  if (error || !idea) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-2">{error || 'Idea not found'}</p>
          <Link to="/dashboard" className="text-brand-400 hover:text-brand-300 text-sm">
            Back to Ideas
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-800 flex-shrink-0 bg-gray-900/50">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-1.5 text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-sm font-semibold text-gray-100 leading-tight">{idea.title}</h1>
            {idea.archetype_id && (
              <span className="text-[11px] text-gray-500">
                {idea.archetype_id.replace(/_/g, ' ')}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Mode toggle */}
          {(['chat', 'edit', 'split', 'todos', 'claims'] as LayoutMode[]).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`px-3 py-1 text-xs font-medium rounded transition flex items-center gap-1.5 ${
                mode === m
                  ? 'bg-brand-600 text-white'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
              }`}
            >
              {MODE_LABELS[m]}
              {m === 'todos' && todoCount > 0 && (
                <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded-full ${
                  mode === 'todos' ? 'bg-brand-500 text-white' : 'bg-yellow-900 text-yellow-400'
                }`}>
                  {todoCount}
                </span>
              )}
              {m === 'claims' && claims.length > 0 && (
                <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded-full ${
                  mode === 'claims' ? 'bg-brand-500 text-white' : 'bg-gray-700 text-gray-400'
                }`}>
                  {claims.length}
                </span>
              )}
            </button>
          ))}

          <div className="w-px h-5 bg-gray-700 mx-1" />

          {/* Conversations toggle */}
          <button
            onClick={() => setConvoSidebarOpen(!convoSidebarOpen)}
            className={`p-1.5 rounded transition ${
              convoSidebarOpen
                ? 'text-blue-400 bg-blue-900/20'
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
            }`}
            title="Toggle conversations"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          </button>

          {/* Scratchpad toggle */}
          <button
            onClick={() => setScratchpadOpen(!scratchpadOpen)}
            className={`p-1.5 rounded transition ${
              scratchpadOpen
                ? 'text-yellow-400 bg-yellow-900/20'
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
            }`}
            title="Toggle scratchpad"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>

          {/* Tests link */}
          <Link
            to={`/ideas/${id}/tests`}
            className="p-1.5 text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded transition"
            title="Run tests"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </Link>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex min-h-0">
        {/* Scratchpad panel (left) */}
        {scratchpadOpen && (
          <div className="w-72 border-r border-gray-800 flex-shrink-0 bg-gray-950">
            <Scratchpad ideaId={id!} />
          </div>
        )}

        {/* Conversation sidebar */}
        {convoSidebarOpen && (
          <div className="w-56 border-r border-gray-800 flex-shrink-0 bg-gray-950">
            <ConversationSidebar
              ideaId={id}
              activeConversationId={conversation?.id}
              onSelectConversation={handleSelectConversation}
              onCreateConversation={handleCreateConversation}
            />
          </div>
        )}

        {/* Chat panel */}
        {(mode === 'chat' || mode === 'split') && (
          <div className="flex-1 flex flex-col min-h-0 min-w-0">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-sm">
                    Start a conversation about your idea.
                  </p>
                  <p className="text-gray-600 text-xs mt-1">
                    Ask for feedback, explore possibilities, or get help refining your concept.
                  </p>
                </div>
              )}
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg px-4 py-3 text-sm ${
                      msg.role === 'user'
                        ? 'bg-brand-600 text-white'
                        : 'bg-gray-800 text-gray-200'
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-gray-800 rounded-lg px-4 py-3">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Chat input */}
            <div className="border-t border-gray-800 p-3 flex-shrink-0">
              <div className="flex gap-2">
                <textarea
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleSendMessage()
                    }
                  }}
                  placeholder="Ask about your idea..."
                  className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-sm text-gray-200 placeholder-gray-500 resize-none outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  rows={1}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!chatInput.trim() || sending}
                  className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Canon editor panel */}
        {(mode === 'edit' || mode === 'split') && (
          <div className={`flex flex-col min-h-0 ${mode === 'split' ? 'w-1/2 border-l border-gray-800' : 'flex-1'}`}>
            {/* Doc tabs */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-800 flex-shrink-0">
              <div className="flex gap-1">
                {(['CB', 'SP', 'BP'] as DocType[]).map((doc) => {
                  const hasContent = editedCanon[doc].trim().length > 50
                  return (
                    <button
                      key={doc}
                      onClick={() => setActiveDoc(doc)}
                      className={`px-3 py-1.5 text-xs font-medium rounded transition flex items-center gap-1.5 ${
                        activeDoc === doc
                          ? 'bg-brand-600 text-white'
                          : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                      }`}
                    >
                      <div
                        className={`w-2 h-2 rounded-full ${
                          hasContent ? 'bg-emerald-400' : 'bg-gray-600'
                        }`}
                      />
                      {doc}
                    </button>
                  )
                })}
              </div>
              <div className="flex items-center gap-2">
                {canonIsDirty && (
                  <span className="text-xs text-orange-400">Unsaved changes</span>
                )}
                <button
                  onClick={handleSaveCanon}
                  disabled={!canonIsDirty || savingCanon}
                  className="px-3 py-1 text-xs font-medium bg-brand-600 hover:bg-brand-700 text-white rounded transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingCanon ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>

            {/* Doc info */}
            <div className="px-4 py-2 border-b border-gray-800/50 flex-shrink-0">
              <h3 className="text-sm font-medium text-gray-200">{DOC_INFO[activeDoc].title}</h3>
              <p className="text-xs text-gray-500">{DOC_INFO[activeDoc].description}</p>
            </div>

            {/* Editor */}
            <textarea
              value={editedCanon[activeDoc]}
              onChange={(e) =>
                setEditedCanon((prev) => ({ ...prev, [activeDoc]: e.target.value }))
              }
              placeholder={`Write your ${DOC_INFO[activeDoc].title} here...`}
              className="flex-1 w-full bg-transparent text-gray-200 text-sm p-4 resize-none outline-none placeholder-gray-600 leading-relaxed font-mono"
              spellCheck={false}
            />
          </div>
        )}

        {/* TODOs panel */}
        {mode === 'todos' && (
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-800 flex-shrink-0">
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-medium text-gray-200">
                  Pending TODOs
                </h3>
                <span className="px-2 py-0.5 text-xs font-medium bg-gray-800 text-gray-400 rounded">
                  {todos.length}
                </span>
              </div>
              <button
                onClick={loadTodos}
                disabled={todosLoading}
                className="px-3 py-1 text-xs text-gray-400 hover:text-gray-300 border border-gray-700 hover:border-gray-600 rounded transition"
              >
                {todosLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {todosLoading && todos.length === 0 ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500" />
                  <p className="mt-2 text-gray-500 text-sm">Loading TODOs...</p>
                </div>
              ) : todos.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-gray-400 text-sm font-medium">No pending TODOs</p>
                  <p className="text-gray-600 text-xs mt-1">
                    {idea.user_id === 'system'
                      ? 'The metabolism pipeline has no pending decisions for this Idea.'
                      : 'TODOs are generated by the agent system for paradigm Ideas.'}
                  </p>
                </div>
              ) : (
                todos.map((todo) => (
                  <TodoCard key={todo.id} todo={todo} onResolve={handleResolveTodo} />
                ))
              )}
            </div>
          </div>
        )}

        {/* Claims panel */}
        {mode === 'claims' && (
          <div className="flex-1 flex flex-col min-h-0">
            {/* Claims header with filters */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-800 flex-shrink-0">
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-medium text-gray-200">Claim Graph</h3>
                <span className="px-2 py-0.5 text-xs font-medium bg-gray-800 text-gray-400 rounded">
                  {displayClaims.length}{claimTypeFilter !== 'all' ? ` / ${claims.length}` : ''}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={claimTypeFilter}
                  onChange={(e) => setClaimTypeFilter(e.target.value)}
                  className="px-2 py-1 text-xs bg-gray-900 border border-gray-700 rounded text-gray-300"
                >
                  <option value="all">All types</option>
                  {claimTypes.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <select
                  value={claimSort}
                  onChange={(e) => setClaimSort(e.target.value as 'section' | 'fidelity')}
                  className="px-2 py-1 text-xs bg-gray-900 border border-gray-700 rounded text-gray-300"
                >
                  <option value="section">By section</option>
                  <option value="fidelity">Lowest fidelity first</option>
                </select>
                <button
                  onClick={loadClaims}
                  disabled={claimsLoading}
                  className="px-3 py-1 text-xs text-gray-400 hover:text-gray-300 border border-gray-700 hover:border-gray-600 rounded transition"
                >
                  {claimsLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {claimsLoading && claims.length === 0 ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500" />
                  <p className="mt-2 text-gray-500 text-sm">Loading claims...</p>
                </div>
              ) : claims.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <p className="text-gray-400 text-sm font-medium">No claims yet</p>
                  <p className="text-gray-600 text-xs mt-1">
                    {idea.user_id === 'system'
                      ? 'This Idea does not have a claims.yaml file yet. Claims are generated from YAML canon documents.'
                      : 'Claims are built from canon documents by the metabolism pipeline. This feature is available for paradigm Ideas.'}
                  </p>
                </div>
              ) : (
                displayClaims.map((claim) => (
                  <ClaimRow key={claim.id} claim={claim} />
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// --- Claim Row Component ---

function ClaimRow({ claim }: { claim: Claim }) {
  const [expanded, setExpanded] = useState(false)
  const typeClass = TYPE_COLORS[claim.type] || TYPE_COLORS.unknown

  return (
    <div
      className="bg-gray-900 rounded border border-gray-800 hover:border-gray-700 transition cursor-pointer"
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-start gap-3 px-3 py-2">
        {/* Claim ID */}
        <span className="px-1.5 py-0.5 text-[10px] font-mono font-bold bg-gray-800 text-gray-300 rounded flex-shrink-0 mt-0.5">
          {claim.id}
        </span>

        {/* Type badge */}
        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded border flex-shrink-0 mt-0.5 ${typeClass}`}>
          {claim.type}
        </span>

        {/* Statement (truncated unless expanded) */}
        <p className={`text-xs text-gray-300 leading-relaxed flex-1 ${expanded ? '' : 'line-clamp-2'}`}>
          {claim.statement}
        </p>

        {/* Fidelity */}
        <div className={`flex items-center gap-1.5 flex-shrink-0 mt-0.5 px-1.5 py-0.5 rounded border text-[10px] font-bold ${fidelityBg(claim.fidelity)}`}>
          <span className={fidelityColor(claim.fidelity)}>
            {(claim.fidelity * 100).toFixed(0)}%
          </span>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-gray-800/50 space-y-2">
          <div className="flex flex-wrap gap-2 text-[10px]">
            <span className="text-gray-500">Section: <span className="text-gray-400">{claim.section}</span></span>
            <span className="text-gray-500">Source: <span className="text-gray-400">{claim.source}</span></span>
            {claim.testable && <span className="text-emerald-600">Testable</span>}
          </div>

          {claim.parents.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-gray-500">Parents:</span>
              {claim.parents.map((p) => (
                <span key={p} className="px-1.5 py-0.5 text-[10px] font-mono bg-gray-800 text-gray-400 rounded">
                  {p}
                </span>
              ))}
            </div>
          )}

          {claim.staleness_tags && claim.staleness_tags.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-gray-500">Staleness:</span>
              {claim.staleness_tags.map((tag) => (
                <span key={tag} className="px-1.5 py-0.5 text-[10px] bg-orange-950 text-orange-400 border border-orange-800 rounded">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
